import { GenericResponse } from '@/types/downloadReports';

import { createAsyncAction } from 'typesafe-actions';

// ---- Types ----
export type ReportCenterRequestPayload = Record<string, any>;

export type ReportCenterSuccessPayload = {
  data: GenericResponse;
};
export interface CreateNewClassificationTagResponse {
  success: boolean;
  result: {
    success: boolean;
    data: {
      status_code: number;
      success: boolean;
      data: {
        classification_tag_id: number;
      };
      message: string;
    };
  };
}

// ---- Actions ----

export const getClusters = createAsyncAction(
  'reportCenter/getClusters/request',
  'reportCenter/getClusters/success',
  'reportCenter/getClusters/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const getClassificationTagsData = createAsyncAction(
  'reportCenter/getClassificationTagsData/request',
  'reportCenter/getClassificationTagsData/success',
  'reportCenter/getClassificationTagsData/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const createNewClassificationTag = createAsyncAction(
  'reportCenter/createNewClassificationTag/request',
  'reportCenter/createNewClassificationTag/success',
  'reportCenter/createNewClassificationTag/failure',
)<
  CreateNewClassificationTagPayload,
  CreateNewClassificationTagResponse,
  undefined
>();

export const getNewClusterData = createAsyncAction(
  'reportCenter/getNewClusterData/request',
  'reportCenter/getNewClusterData/success',
  'reportCenter/getNewClusterData/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const createClusterAndRelationship = createAsyncAction(
  'reportCenter/createClusterAndRelationship/request',
  'reportCenter/createClusterAndRelationship/success',
  'reportCenter/createClusterAndRelationship/failure',
)<
  void,
  CreateClusterAndRelationshipSuccess,
  CreateClusterAndRelationshipError
>();

export const getReportsForClusterData = createAsyncAction(
  'reportCenter/getReportsForClusterData/request',
  'reportCenter/getReportsForClusterData/success',
  'reportCenter/getReportsForClusterData/failure',
)<
  GetReportsForClusterDataPayload,
  GetReportsForClusterDataSuccessPayload,
  string
>();

export const createNewReportsForCluster = createAsyncAction(
  'reportCenter/createNewReportsForCluster/request',
  'reportCenter/createNewReportsForCluster/success',
  'reportCenter/createNewReportsForCluster/failure',
)<
  CreateNewReportsForClusterPayload,
  { clusterId: number },
  { error: string }
>();

export const createDashboardClassClusterReport = createAsyncAction(
  'reportCenter/createDashboardClassClusterReport/request',
  'reportCenter/createDashboardClassClusterReport/success',
  'reportCenter/createDashboardClassClusterReport/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const updateRegularClusterReport = createAsyncAction(
  'reportCenter/updateRegularClusterReport/request',
  'reportCenter/updateRegularClusterReport/success',
  'reportCenter/updateRegularClusterReport/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const updateSponsorDashReport = createAsyncAction(
  'reportCenter/updateSponsorDashReport/request',
  'reportCenter/updateSponsorDashReport/success',
  'reportCenter/updateSponsorDashReport/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const deleteClusterFrequencyReport = createAsyncAction(
  'reportCenter/deleteClusterFrequencyReport/request',
  'reportCenter/deleteClusterFrequencyReport/success',
  'reportCenter/deleteClusterFrequencyReport/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const getClassTagsTableData = createAsyncAction(
  'reportCenter/getClassTagsTableData/request',
  'reportCenter/getClassTagsTableData/success',
  'reportCenter/getClassTagsTableData/failure',
)<undefined, ReportCenterSuccessPayload, undefined>();

export const updateClassTagData = createAsyncAction(
  'reportCenter/updateClassTagData/request',
  'reportCenter/updateClassTagData/success',
  'reportCenter/updateClassTagData/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const getClustersTableData = createAsyncAction(
  'reportCenter/getClustersTableData/request',
  'reportCenter/getClustersTableData/success',
  'reportCenter/getClustersTableData/failure',
)<undefined, ReportCenterSuccessPayload, undefined>();

export const updateClustersTableData = createAsyncAction(
  'reportCenter/updateClustersTableData/request',
  'reportCenter/updateClustersTableData/success',
  'reportCenter/updateClustersTableData/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const generateOnDemandReport = createAsyncAction(
  'reportCenter/generateOnDemandReport/request',
  'reportCenter/generateOnDemandReport/success',
  'reportCenter/generateOnDemandReport/failure',
)<OnDemandReportPayload, OnDemandReportAcknowledgement, undefined>();

export const getSavedReportsForClusterData = createAsyncAction(
  'reportCenter/getSavedReportsForClusterData/request',
  'reportCenter/getSavedReportsForClusterData/success',
  'reportCenter/getSavedReportsForClusterData/failure',
)<undefined, GetSavedReportsForClusterDataSuccessPayload, undefined>();

export interface OnDemandReportPayload {
  report_cluster_frequency_id: number;
  is_monte_carlo?: boolean;
}

// Response after requesting a report (based on OnDemandAcknowledgementSerializer)
export interface OnDemandReportAcknowledgement {
  message: string; // e.g., "Acknowledgement email sent successfully"
  status?: string; // optional, depending on serializer fields
}

interface BaseClusterPayload {
  cluster_id?: string;
  user_id?: string;
}
export interface GetReportsForClusterDataMinimalPayload {
  user_id: string; // only required field
}

export interface GetReportsForClusterDataPayload {
  user_id: string;
}
export interface GetReportsForClusterDataSuccessPayload {
  clusters: Cluster[];
  frequencies: Frequency[];
  emails: string[];
  reports: Reporttype[];
  monte_carlo_report?: Reporttype | null;
}
export interface Cluster {
  id: number;
  cluster_name: string;
  is_sftp: number;
  is_email: number;
  calendar_pmca: {
    calendar_pmca_id: number;
    calendar_name: string;
    calendar: number;
  };
  classification_tags: {
    classification_tag_id: string;
    sponsor_id: number | null;
  }[];
}

export interface Frequency {
  id: number;
  report_name: string;
  description: string | null;
}

export interface Reporttype {
  report_id: number;
  report_code?: string | null;
  report_name: string;
  report_description: string | null;
  display_name: string;
}

interface BaseReportPayload {
  report_id?: string;
  user_id?: string;
}

// Cluster-related interfaces
export interface GetClustersPayload {
  filters?: {
    status?: string;
    date_range?: {
      start_date: string;
      end_date: string;
    };
    classification_tags?: string[];
  };
  pagination?: {
    page: number;
    page_size: number;
  };
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface GetNewClusterDataPayload {
  cluster_type?: string;
  template_id?: string;
  user_id: string;
}

export interface CreateClusterAndRelationshipPayload {
  user_id: string;
  data: {
    cluster_name: string;
    calendar_pmca_id?: number;
    classification_tag_ids: string[];
    is_email: boolean;
    is_sftp: boolean;
  };
}
export interface CreateClusterAndRelationshipSuccess {
  data: {
    cluster_id: number;
  };
}

export interface CreateClusterAndRelationshipError {
  message: string;
}
export interface UpdateClustersTableDataPayload extends BaseClusterPayload {
  cluster_name?: string;
  description?: string;
  status?: string;
  classification_tag_ids?: string[];
  metadata?: Record<string, any>;
}

// Classification Tag interfaces
export interface GetClassificationTagsDataPayload {
  filters?: {
    active_only?: boolean;
    tag_type?: string;
    search_term?: string;
  };
  pagination?: {
    page: number;
    page_size: number;
  };
}

export interface CreateNewClassificationTagPayload {
  user_id: string;
  data: {
    class_tag_name: string;
    comp_entity_id: string;
    base_currency_id: number;
    report_currency_id: number;
    fund_company_id: string | null;
    classification_currency_id: number;
    fund_mifid_inclusion_flag: boolean;
    executing_lei: string;
    trade_tag_id: number;
    company_id?: string | null;
  };
}

export interface UpdateClassTagDataPayload {
  user_id: string;
  data: {
    class_tag_id: number;
    comp_entity_id: string;
    trade_tag_id: number;
    base_currency_id: number;
    report_currency_id: number;
    classification_currency_id: number;
    fund_company_name: string;
    fund_company_id: string | null;
    class_tag_name?: string;
    mifid_executing_lei?: string;
    fund_mifid_inclusion_flag?: boolean;

    classification_flag?: boolean;
    fund_company_percentage_flag?: boolean;
    company_id?: string | null;
  };
}

// Reports-related interfaces
export interface GetReportsForClusterDataPayload extends BaseClusterPayload {
  filters?: {
    report_type?: string;
    status?: string;
    date_range?: {
      start_date: string;
      end_date: string;
    };
  };
  pagination?: {
    page: number;
    page_size: number;
  };
}

export interface CreateNewReportsForClusterPayload {
  user_id: string;
  data: {
    clusterId: number;
    frequency: number;
    isEmail: boolean;
    isSftp: boolean;
    emails: string[];
    report: number[];
    schedule_anchor_date?: string;
  };
}

export interface CreateDashboardClassClusterReportPayload {
  dashboard_id: string;
  cluster_ids: string[];
  classification_tag_ids: string[];
  report_name: string;
  description?: string;
  layout_config?: Record<string, any>;
  user_id: string;
}

export interface UpdateRegularClusterReportPayload {
  user_id: string;
  data: {
    reportClusterFreqId: number;
    clusterId: number;
    frequency: number;
    isEmail: boolean;
    isSftp: boolean;
    emails: string[];
    report: number[];
    schedule_anchor_date?: string;
  };
}

export interface UpdateSponsorDashReportPayload extends BaseReportPayload {
  sponsor_id?: string;
  dashboard_config?: Record<string, any>;
  visibility_settings?: {
    is_public: boolean;
    allowed_users?: string[];
    allowed_groups?: string[];
  };
  refresh_interval?: number;
}

export interface DeleteClusterFrequencyReportPayload {
  user_id: string;
  data: {
    reportClusterFreqId: string;
  };
}

// getReportsForClusterData
export interface SavedReportsCluster {
  id: number;
  cluster_name: string;
  is_sftp: number;
  is_email: number;
  calendar_pmca: {
    calendar_pmca_id: number;
    calendar_name: string;
    calendar: number;
  };
  classification_tags: {
    classification_tag_id: string;
    sponsor_id: string | null;
  }[];
}

export interface SavedReportsFrequency {
  id: number;
  report_name: string;
  description: string | null;
}

export interface SavedReportsDefinition {
  report_id: number;
  report_code?: string | null;
  report_name: string;
  report_description: string;
  display_name: string;
}

export interface GetSavedReportsForClusterDataSuccessPayload {
  clusters: SavedReportsCluster[];
  frequencies: SavedReportsFrequency[];
  emails: string[];
  reports: SavedReportsDefinition[];
  monte_carlo_report?: SavedReportsDefinition | null;
}
